import React, { useEffect, useState } from 'react';

import { PanelsEnum } from '@ariksa/inventory-core/api';
import {
  Box,
  ListItem,
  Stack,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react';
import { each, every, orderBy } from 'lodash';
import { useSelector } from 'react-redux';

import { formatNumberWithComma, formatTooltip } from 'components/DataDisplay';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { IconTypes } from 'components/Icons';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { AllEntitiesDrawer } from 'containers/Dashboard/Drawers/AllEntitiesDrawer';
import { selectIdentityOverview } from 'containers/Dashboard/IdentityOverview/selectors';
import { AllEntitiesDetailsProps } from 'containers/Dashboard/types';
import { renderNoEntitiesFoundWithIcon } from 'containers/Dashboard/utils';
import { DeployOutpostForDataMessage } from 'containers/Dashboard/utils/DeployOutpostForDataMessage';
import {
  DashboardWidgetTooltipIcon,
  renderResourcesWithIconAndCount,
} from 'containers/Dashboard/utils/utils';
import { selectSharedState } from 'containers/SharedState/selectors';

import { customTheme } from '../../../../../theme';

export const IdentitiesWithAccessToSensitiveData: React.FC = () => {
  const { identitiesWithAccessToSensitiveData } = useSelector(
    selectIdentityOverview,
  );
  const [data, setData] = useState<Record<string, any>>({});
  const allEntitiesDisclosure = useDisclosure();
  const [details, setDetails] = useState<AllEntitiesDetailsProps>(
    {} as AllEntitiesDetailsProps,
  );
  const { isDataScannerDeployed } = useSelector(selectSharedState);
  const { environmentId } = useAccessBoundary();
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let items = {};
    each(
      identitiesWithAccessToSensitiveData.data?.items,
      o => (items[o.provider_or_source_type + ' ' + o.agnostic] = o.count),
    );
    setData(items);
  }, [identitiesWithAccessToSensitiveData.data]);

  useEffect(() => {
    setTotal(
      (data?.['Cloud User'] ?? 0) +
        (data?.['Cloud Role'] ?? 0) +
        (data?.['Cross Account Role'] ?? 0) +
        (data?.['Third Party Account'] ?? 0),
    );
  }, [data]);

  const categories = [
    {
      label: 'Users',
      iconType: IconTypes.IamUser,
      count: data?.['Cloud User'] || 0,
      isLoading: identitiesWithAccessToSensitiveData.isLoading,
      iconColor: customTheme.colors.blue['300'],
      onClick: () => {
        allEntitiesDisclosure.onOpen();
        setDetails({
          panel: PanelsEnum.SensitiveDataUsers,
          iconType: IconTypes.IamUser,
          total: data?.['Cloud User'] || 0,
        });
      },
      iconTooltip: formatTooltip({
        header: 'Users',
        content: 'Cloud-native and federated users',
        width: 32,
      }),
    },
    {
      label: 'Roles',
      iconType: IconTypes.Role,
      count: data?.['Cloud Role'] || 0,
      isLoading: identitiesWithAccessToSensitiveData.isLoading,
      onClick: () => {
        allEntitiesDisclosure.onOpen();
        setDetails({
          panel: PanelsEnum.SensitiveDataRoles,
          // Roles drawer previously used the IAM user icon; use the role icon.
          iconType: IconTypes.Role,
          total: data?.['Cloud Role'] || 0,
        });
      },
      iconTooltip: formatTooltip({
        header: 'Roles',
        content: 'Cloud-native roles for public clouds, SaaS',
        width: 32,
      }),
    },
    {
      label: 'Cross-Account',
      iconType: IconTypes.CrossAccount,
      count: data?.['Cross Account Role'] || 0,
      isLoading: identitiesWithAccessToSensitiveData.isLoading,
      iconTooltip: formatTooltip({
        header: 'Cross account',
        content: 'Other accounts that have been onboarded',
        width: 32,
      }),
      onClick: () => {
        allEntitiesDisclosure.onOpen();
        setDetails({
          panel: PanelsEnum.SensitiveDataCrossAccount,
          iconType: IconTypes.CrossAccount,
          total: data?.['Cross Account Role'] || 0,
        });
      },
    },
    {
      label: 'Third-party',
      iconType: IconTypes.ThirdParty,
      count: data?.['Third Party Account'] || 0,
      isLoading: identitiesWithAccessToSensitiveData.isLoading,
      iconTooltip: formatTooltip({
        header: 'Third-party',
        content: 'Other accounts that have not been onboarded',
        width: 32,
      }),
      onClick: () => {
        allEntitiesDisclosure.onOpen();
        setDetails({
          panel: PanelsEnum.SensitiveDataThirdParty,
          iconType: IconTypes.ThirdParty,
          total: data?.['Third Party Account'] || 0,
        });
      },
    },
  ];

  return (
    <>
      <DashboardWidget
        label="Identities with Sensitive Data Access"
        noData={every(Object.values(data), o => !o)}
        noDataMessage={renderNoEntitiesFoundWithIcon('No identities found')}
        isLoading={identitiesWithAccessToSensitiveData.isLoading}
        content={
          isDataScannerDeployed?.[environmentId!] ? (
            renderResourcesWithIconAndCount(
              orderBy(categories, ['count'], ['desc']),
            )
          ) : (
            <DeployOutpostForDataMessage />
          )
        }
        styles={{ contentWrapper: { px: 0, pb: 0 } }}
        tooltip={{
          tooltip: (
            <Stack spacing={3}>
              <Box>
                Ariksa automatically assess identity risks to sensitive data by
                evaluating permissions, policies and paths to data.
              </Box>
              <Box>To right-size identity access:</Box>
              <UnorderedList color="primary" spacing={2}>
                <ListItem pl={2}>Deploy Outpost for Data</ListItem>
                <ListItem pl={2}>Retain user activity logs</ListItem>
                <ListItem pl={2}>Remediate using Ariksa</ListItem>
              </UnorderedList>
            </Stack>
          ),
          header: 'Identities with Sensitive Data Access',
          showArrow: false,
          button: <DashboardWidgetTooltipIcon />,
        }}
        subHeader={formatNumberWithComma(total) + ' identities'}
      />
      <AllEntitiesDrawer
        isOpen={allEntitiesDisclosure.isOpen}
        onClose={allEntitiesDisclosure.onClose}
        {...details}
      />
    </>
  );
};
